import { useEffect, useRef } from 'react';
import { isFreshMatch, touchNamesIndex } from '@/game/view/names';
import { NO_SLOTS, parseSlots, SLOTS_INDEX_KEY, type Slots, slotsKey } from '@/game/view/slots';
import type { UiStore } from './store';

/**
 * This match's control groups from localStorage. As with names
 * (`loadNames`), a match that has only just begun (`tick`) clears what's
 * stored under its key (a rematch in the same room), while a reload
 * mid-match restores it. Storage may be unavailable: none then.
 */
export function loadSlots(key: string, tick: number): Slots {
  try {
    if (isFreshMatch(tick)) {
      localStorage.removeItem(key);
      return NO_SLOTS;
    }
    return parseSlots(localStorage.getItem(key));
  } catch {
    return NO_SLOTS;
  }
}

/** Stores `slots` under `key` and drops the slot sets of all but the most recent matches. */
export function saveSlots(key: string, slots: Slots) {
  try {
    if (Object.keys(slots).length === 0) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify(slots));
    const { index, drop } = touchNamesIndex(localStorage.getItem(SLOTS_INDEX_KEY), key, Date.now());
    for (const old of drop) localStorage.removeItem(old);
    localStorage.setItem(SLOTS_INDEX_KEY, JSON.stringify(index));
  } catch {
    // Private mode / blocked storage: keep the in-memory slots only.
  }
}

/**
 * Keeps the store's control groups (`slots`) in localStorage for this match
 * and player (`slotsKey`): loaded during the first render and saved on every
 * change, like `usePersistedNames`. Nothing is stored without a seed.
 */
export function usePersistedSlots(ui: UiStore, seed: string, playerId: string, tick: number) {
  const key = seed === '' ? null : slotsKey(seed, playerId);
  const loaded = useRef<string | null>(null);
  if (key !== null && loaded.current !== key) {
    loaded.current = key;
    ui.set({ slots: loadSlots(key, tick) });
  }
  useEffect(() => {
    if (key === null) return;
    let saved = ui.get().slots;
    return ui.subscribe(() => {
      const { slots } = ui.get();
      if (slots === saved) return;
      saved = slots;
      saveSlots(key, slots);
    });
  }, [ui, key]);
}
